// src/components/LogoutBottomSheet.jsx
import React, { useState, useEffect, useRef } from 'react';
import { IoLogOutOutline, IoClose } from "react-icons/io5";

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export default function LogoutBottomSheet({ onConfirm }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const scrollYRef = useRef(0);

  // Open via custom event
  useEffect(() => {
    const openSheet = () => {
      scrollYRef.current = window.scrollY || window.pageYOffset;
      setSheetOpen(true);
    };
    window.addEventListener('open-logout-sheet', openSheet);
    return () => window.removeEventListener('open-logout-sheet', openSheet);
  }, []);

  const handleCancel = () => setSheetOpen(false);
  const handleConfirm = () => { onConfirm?.(); setSheetOpen(false); };

  // Freeze + theme-color (match other sheets)
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (sheetOpen) {
      // lock page at current scroll
      document.body.style.top = `-${scrollYRef.current}px`;
      document.body.classList.add('overlay-active');
      document.documentElement.classList.add('overlay-active');
      document.getElementById('root')?.classList.add('overlay-active');
      // notch/status bar
      if (meta) meta.content = '#999998';
    } else {
      // restore
      const y = scrollYRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);
      if (meta) meta.content = '#ffffff';
    }
    return () => {
      // cleanup on unmount
      const y = scrollYRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);
      const m = document.querySelector('meta[name="theme-color"]');
      if (m) m.content = '#ffffff';
    };
  }, [sheetOpen]);

  if (!sheetOpen) return null;

  return (
    <>
      <div style={S.backdrop} onClick={handleCancel}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Log out confirmation"
          style={{
            ...S.sheet,
            paddingBottom: `calc(12px + env(safe-area-inset-bottom,0px) + ${isPWA() ? 8 : 0}px)`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top bar */}
          <div style={S.topBar}>
            <div style={S.pill}>Confirm</div>
            <button aria-label="Close" style={S.iconBtn} onClick={handleCancel}>
              {/* centered X */}
              <IoClose size={18} style={{ display: 'block' }} />
            </button>
          </div>

          {/* Icon + copy */}
          <div style={S.hero}>
            <div style={S.iconWrap}><IoLogOutOutline size={28} /></div>
            <h3 style={S.title}>Log out?</h3>
            <p style={S.desc}>Are you sure you want to log out? You can log back in anytime.</p>
          </div>

          {/* CTA row */}
          <div style={S.ctaRow}>
            <button type="button" onClick={handleCancel} style={S.secondaryBtn}>
              Cancel
            </button>
            <button type="button" onClick={handleConfirm} style={S.destructiveBtn}>
              Confirm
            </button>
          </div>

          <style>{`
            @keyframes slideUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
            @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
            button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
          `}</style>
        </div>
      </div>
    </>
  );
}

/* ───────────────────────── Styles (Uber-like tokens) */
const S = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.40)', // aligned with other sheets
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .22s ease-out',
    padding: 16,
    minHeight: 'clamp(340px, 40vh, 520px)',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
  },
  pill: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    color: '#0b0b0b',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 800,
  },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32, height: 32,
    display: 'inline-flex',           // true centering
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  hero: {
    textAlign: 'center',
    padding: '16px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  iconWrap: {
    width: 56, height: 56, borderRadius: 999,
    margin: '6px auto 10px',
    display: 'grid', placeItems: 'center',
    background: '#FEF2F2',
    color: '#B91C1C',
    border: '1px solid #FEE2E2',
  },
  title: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 900,
    color: '#0b0b0b',
  },
  desc: {
    margin: '6px auto 0',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#4b5563',
    maxWidth: 360,
  },
  ctaRow: {
    display: 'flex',
    gap: 8,
    marginTop: 'auto',
  },
  secondaryBtn: {
    flex: 1,
    background: '#ffffff',
    color: '#0b0b0b',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
  destructiveBtn: {
    flex: 1,
    background: '#B91C1C',
    color: '#ffffff',
    border: '1px solid #B91C1C',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
};
